import {
  PropertySharingFieldPart,
  PropertySharingTextPart,
} from 'src/api/generated';
import { useEffect, useId, useRef, useState } from 'react';
import { PiPencilSimple } from 'react-icons/pi';
import type {
  PropertySharingTemplatePart,
  PropertySharingExternalSite,
} from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { UnsavedChangesModal } from 'src/modules/shared/components/molecules/modals/unsaved-changes-modal';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type {
  PropertyLinkSetupModalProps,
  PropertyLinkPartLocation,
} from '../../types/property-links.types';
import {
  parsePropertyLinkExample,
  propertyLinkParameterKey,
  propertyLinkParameters,
  replacePropertyLinkPart,
  splitPropertyLinkPath,
} from '../../utils/property-link-template.util';
import { propertyLinksFormSchema } from '../../validations/property-links.validation';
import { PropertyLinkPartBinding } from '../molecules/property-links/property-link-part-binding';
import { PropertyLinkEditorStep } from '../molecules/property-links/property-link-editor-step';
import { PropertyLinkExamplePreview } from '../molecules/property-links/property-link-example-preview';

/** Configure an isolated external-link draft; only the final action persists the owning policy. */
export function PropertyLinkSetupModal({
  site: storedSite,
  identifiers,
  disabled,
  saving,
  saveError,
  identifierActions,
  onSave,
  onClose,
  onDirtyChange,
}: PropertyLinkSetupModalProps) {
  const { t } = useI18n();
  const initialSite = {
    ...storedSite,
    path: splitPropertyLinkPath(storedSite.path),
  };
  const [site, setSite] = useState(initialSite);
  const [step, setStep] = useState(initialSite.origin ? 2 : 1);
  const [example, setExample] = useState('');
  const [invalidExample, setInvalidExample] = useState(false);
  const [replacement, setReplacement] =
    useState<PropertySharingExternalSite | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [bindingDirty, setBindingDirty] = useState(false);
  const [bindingValid, setBindingValid] = useState(false);
  const [selection, setSelection] = useState<PropertyLinkPartLocation | null>(
    null
  );
  const bindingId = useId();
  const bindingFormId = useId();
  const selectionTrigger = useRef<HTMLButtonElement | null>(null);
  const bindingRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (selection === null) selectionTrigger.current?.focus();
    else bindingRef.current?.focus();
  }, [selection]);
  const dirty =
    Boolean(example) ||
    bindingDirty ||
    JSON.stringify(site) !== JSON.stringify(initialSite);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  useEffect(() => {
    bodyRef.current?.focus();
  }, [step]);
  const parameters = propertyLinkParameters(site);
  const validated = propertyLinksFormSchema.shape.externalSite.safeParse(site);
  const selectedPart = selection
    ? (selection.queryIndex === null
        ? site.path
        : site.query.at(selection.queryIndex)?.value ?? []
      ).at(selection.partIndex)
    : undefined;
  const label = (part: PropertySharingTemplatePart) =>
    part.kind === PropertySharingTextPart.kind.TEXT
      ? part.value
      : part.kind === PropertySharingFieldPart.kind.PROPERTY
        ? t(`propertyLinks.field.${part.field}`)
        : `${part.selector.label ?? t('propertyLinks.unlabelled')} · ${part.selector.system}`;
  const closeSelection = () => {
    setSelection(null);
    setBindingDirty(false);
    setBindingValid(false);
  };
  const applyExample = (parsed: PropertySharingExternalSite) => {
    setSite(parsed);
    setExample('');
    setReplacement(null);
    setStep(2);
  };
  const loadExample = () => {
    const parsed = parsePropertyLinkExample(example);
    if (!parsed) {
      setInvalidExample(true);
      return;
    }
    if (parameters.length) setReplacement(parsed);
    else applyExample(parsed);
  };
  const requestClose = () => {
    if (dirty) setConfirmClose(true);
    else onClose();
  };
  const renderParts = (
    parts: PropertySharingTemplatePart[],
    queryIndex: number | null
  ) =>
    parts.map((part, partIndex) =>
      part.kind === PropertySharingTextPart.kind.TEXT &&
      (!part.value || /^\/+$/u.test(part.value)) ? (
        <Text as="span" key={partIndex}>
          {part.value}
        </Text>
      ) : (
        <Button
          key={partIndex}
          size="sm"
          appearance={
            part.kind === PropertySharingTextPart.kind.TEXT ? 'outline' : 'soft'
          }
          variant={
            part.kind === PropertySharingTextPart.kind.TEXT
              ? 'neutral'
              : 'primary'
          }
          disabled={saving || selection !== null}
          className="!h-auto min-h-9 max-w-full !whitespace-normal break-all !text-sm"
          aria-label={`${t('propertyLinks.editPortion')}: ${label(part)}`}
          aria-expanded={
            selection?.queryIndex === queryIndex &&
            selection.partIndex === partIndex
          }
          aria-controls={selectedPart ? bindingId : undefined}
          onClick={(event) => {
            selectionTrigger.current = event.currentTarget;
            setSelection({ queryIndex, partIndex });
          }}
        >
          {label(part)}
          <PiPencilSimple
            className="h-3.5 w-3.5 shrink-0 opacity-60"
            aria-hidden
          />
        </Button>
      )
    );
  return (
    <>
      <Modal
        isOpen
        onClose={requestClose}
        dismissible={!saving && !confirmClose && !replacement}
        ariaLabel={t('propertyLinks.configureExternal')}
        dialogClassName="sm:max-w-2xl"
        header={
          <div className="space-y-2">
            <Text size="h6" weight="semibold">
              {t('propertyLinks.configureExternal')}
            </Text>
            <ol
              className="flex gap-3"
              aria-label={t('propertyLinks.setupProgress')}
            >
              {(
                [
                  'propertyLinks.exampleStep',
                  'propertyLinks.markVariables',
                  'propertyLinks.examplePreview',
                ] as const
              ).map((key, index) => (
                <li
                  key={key}
                  aria-current={step === index + 1 ? 'step' : undefined}
                  className={`min-w-0 flex-1 border-b-2 pb-2 ${step === index + 1 ? 'border-primary-500 text-primary-700' : 'border-secondary-200 text-secondary-500'}`}
                >
                  <Text size="tiny">
                    {index + 1}. {t(key)}
                  </Text>
                </li>
              ))}
            </ol>
          </div>
        }
        body={
          <div
            ref={bodyRef}
            tabIndex={-1}
            className="w-full min-w-0 space-y-4 px-2 py-4 outline-none"
          >
            {step === 1 && (
              <PropertyLinkEditorStep
                step={1}
                title={t('propertyLinks.exampleStep')}
                description={t('propertyLinks.pasteExampleHelp')}
              >
                <TextInput
                  aria-label={t('propertyLinks.pasteExample')}
                  type="url"
                  placeholder="https://inmobiliaria.example/propiedades/casa/4512"
                  value={example}
                  disabled={disabled}
                  onChange={(event) => {
                    setExample(event.target.value);
                    setInvalidExample(false);
                  }}
                />
                {invalidExample && (
                  <Alert
                    variant="danger"
                    message={t('propertyLinks.invalidExample')}
                  />
                )}
              </PropertyLinkEditorStep>
            )}
            {step === 2 && (
              <PropertyLinkEditorStep
                step={2}
                title={t('propertyLinks.markVariables')}
                description={
                  selection ? undefined : t('propertyLinks.markVariablesHelp')
                }
              >
                <div
                  hidden={selection !== null}
                  className="rounded-lg border border-secondary-200 p-3 space-y-2"
                >
                  <Text
                    size="small"
                    color="secondary"
                    className="break-all"
                    disableAutoLink
                  >
                    {site.origin}
                  </Text>
                  <div
                    className="flex flex-wrap items-center gap-1"
                    role="group"
                    aria-label={t('propertyLinks.path')}
                  >
                    {renderParts(site.path, null)}
                  </div>
                  {site.query.map((entry, queryIndex) => (
                    <div
                      key={queryIndex}
                      className="flex flex-wrap items-center gap-1"
                      role="group"
                      aria-label={`${t('propertyLinks.queryValue')} ${entry.key}`}
                    >
                      <Text as="span" className="break-all">
                        {queryIndex === 0 ? '?' : '&'}
                        {entry.key}
                        {entry.omitEquals ? '' : '='}
                      </Text>
                      {renderParts(entry.value, queryIndex)}
                    </div>
                  ))}
                </div>
                {!selection && !parameters.length && (
                  <Text size="small" color="secondary">
                    {t('propertyLinks.needsVariable')}
                  </Text>
                )}
                {selectedPart && selection && (
                  <div
                    id={bindingId}
                    ref={bindingRef}
                    tabIndex={-1}
                    className="outline-none"
                  >
                    <PropertyLinkPartBinding
                      key={`${selection.queryIndex}:${selection.partIndex}`}
                      formId={bindingFormId}
                      part={selectedPart}
                      identifiers={identifiers}
                      disabled={disabled}
                      mappings={site.valueMappings ?? []}
                      encoded={
                        selection.queryIndex === null ||
                        site.query[selection.queryIndex].encoded === true
                      }
                      onDirtyChange={setBindingDirty}
                      onValidityChange={setBindingValid}
                      onApply={(parts, mapping) => {
                        const next = replacePropertyLinkPart(
                          site,
                          selection,
                          parts
                        );
                        if (mapping) {
                          next.valueMappings = [
                            ...(next.valueMappings ?? []).filter(
                              (item) =>
                                propertyLinkParameterKey(item.parameter) !==
                                propertyLinkParameterKey(mapping.parameter)
                            ),
                            ...(Object.keys(mapping.values).length
                              ? [mapping]
                              : []),
                          ];
                        }
                        setSite(next);
                        closeSelection();
                      }}
                    />
                  </div>
                )}
                {identifierActions}
                {!validated.success && parameters.length > 0 && (
                  <Alert
                    variant="warning"
                    message={t(
                      validated.error.issues.some(
                        (issue) => issue.path[0] === 'query'
                      )
                        ? 'propertyLinks.invalidQuery'
                        : 'propertyLinks.invalidTemplate'
                    )}
                  />
                )}
              </PropertyLinkEditorStep>
            )}
            {step === 3 && <PropertyLinkExamplePreview site={site} />}
            {saveError && (
              <Alert variant="danger" message={t('propertyLinks.saveError')} />
            )}
          </div>
        }
        footer={
          <div className="space-y-2 p-4">
            <div className="flex justify-between gap-2">
              <Button
                appearance="ghost"
                variant="neutral"
                disabled={saving}
                onClick={requestClose}
              >
                {t('propertyLinks.cancel')}
              </Button>
              <div className="flex gap-2">
                {step > 1 && (!disabled || step === 3 || selection) && (
                  <Button
                    appearance="outline"
                    variant="neutral"
                    disabled={saving}
                    onClick={() =>
                      selection ? closeSelection() : setStep(step - 1)
                    }
                  >
                    {t('propertyLinks.back')}
                  </Button>
                )}
                {step === 1 && site.origin && (
                  <Button
                    appearance="outline"
                    variant="neutral"
                    disabled={disabled}
                    onClick={() => {
                      setExample('');
                      setInvalidExample(false);
                      setStep(2);
                    }}
                  >
                    {t('propertyLinks.keepExample')}
                  </Button>
                )}
                {step === 1 && (
                  <Button
                    disabled={disabled || !example.trim()}
                    onClick={loadExample}
                  >
                    {t('propertyLinks.useExample')}
                  </Button>
                )}
                {step === 2 && selection && (
                  <Button
                    type="submit"
                    form={bindingFormId}
                    disabled={!bindingValid || saving}
                  >
                    {t('propertyLinks.applyPart')}
                  </Button>
                )}
                {step === 2 && !selection && (
                  <Button
                    disabled={saving || !validated.success}
                    onClick={() => setStep(3)}
                  >
                    {t('propertyLinks.reviewLink')}
                  </Button>
                )}
                {step === 3 && (
                  <Button
                    disabled={disabled || !validated.success}
                    loading={saving}
                    onClick={() => {
                      if (validated.success && validated.data) {
                        onSave(validated.data);
                      }
                    }}
                  >
                    {t('propertyLinks.save')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        }
      />
      <UnsavedChangesModal
        isOpen={confirmClose}
        onProceed={onClose}
        onStay={() => setConfirmClose(false)}
      />
      <Modal
        isOpen={replacement !== null}
        onClose={() => setReplacement(null)}
        compact
        header={
          <Text weight="medium">{t('propertyLinks.replaceExample')}</Text>
        }
        body={<Text>{t('propertyLinks.replaceExampleHelp')}</Text>}
        footer={
          <div className="flex justify-end gap-2 p-3">
            <Button
              appearance="ghost"
              variant="neutral"
              onClick={() => setReplacement(null)}
            >
              {t('propertyLinks.cancel')}
            </Button>
            <Button
              onClick={() => {
                if (replacement) applyExample(replacement);
              }}
            >
              {t('propertyLinks.replaceExample')}
            </Button>
          </div>
        }
      />
    </>
  );
}
